import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Stack,
  Box,
  Flex,
  Title,
  Text,
  Paper,
  Table,
  Select,
  Button,
  Textarea,
  TextInput,
  Loader,
  Alert,
  Center,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { trpc } from '../lib/trpc';

function formatDate(value: string | Date | null | undefined) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatPrice(value: string | number | null | undefined) {
  if (value === null || value === undefined) return '—';
  return Number(value).toFixed(2);
}

interface ContactBlockProps {
  label: string;
  contact: {
    name: string | null;
    street: string | null;
    street2: string | null;
    city: string | null;
    zip: string | null;
    phone: string | null;
    company: string | null;
    vatCode: string | null;
  } | null;
  showVat?: boolean;
}

function ContactBlock({ label, contact, showVat }: ContactBlockProps) {
  return (
    <Paper withBorder p="md" flex={1}>
      <Title order={4} mb="sm">{label}</Title>
      {contact ? (
        <Stack gap={2}>
          {contact.company && <Text>{contact.company}</Text>}
          <Text>{contact.name ?? '—'}</Text>
          {contact.street && <Text>{contact.street}</Text>}
          {contact.street2 && <Text>{contact.street2}</Text>}
          {(contact.zip || contact.city) && (
            <Text>{[contact.zip, contact.city].filter(Boolean).join(' ')}</Text>
          )}
          {contact.phone && <Text>Tel: {contact.phone}</Text>}
          {showVat && contact.vatCode && <Text>VAT: {contact.vatCode}</Text>}
        </Stack>
      ) : (
        <Text c="dimmed">Same as billing</Text>
      )}
    </Paper>
  );
}

export default function OrderDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const orderId = Number(id);

  const utils = trpc.useUtils();

  const { data, isLoading, isError } = trpc.admin.order.getDetail.useQuery(
    { id: orderId },
    { enabled: !Number.isNaN(orderId) },
  );

  const statusListQuery = trpc.admin.crud.list.useQuery({
    entity: 'orderStatus',
    page: 1,
    pageSize: 100,
  });

  const [note, setNote] = useState<string | undefined>(undefined);
  const [trackingLink, setTrackingLink] = useState<string | undefined>(undefined);

  const updateStatusMutation = trpc.admin.order.updateStatus.useMutation({
    onSuccess: () => void utils.admin.order.getDetail.invalidate({ id: orderId }),
  });

  const updateNoteMutation = trpc.admin.order.updateNote.useMutation({
    onSuccess: () => void utils.admin.order.getDetail.invalidate({ id: orderId }),
  });

  const updateTrackingMutation = trpc.admin.order.updateTracking.useMutation({
    onSuccess: () => void utils.admin.order.getDetail.invalidate({ id: orderId }),
  });

  if (Number.isNaN(orderId)) return <Alert color="red">Invalid order ID.</Alert>;
  if (isLoading) return <Center h="60vh"><Loader /></Center>;
  if (isError || !data) return <Alert color="red">Order not found.</Alert>;

  const currentNote = note ?? data.note ?? '';
  const currentTracking = trackingLink ?? data.trackingLink ?? '';

  const statuses = (statusListQuery.data?.rows ?? []) as Array<{ id: unknown; title: unknown }>;

  const subtotal = Number(data.price ?? 0);
  const vat = Number(data.vat ?? 0);
  const deliveryPrice = Number(data.deliveryPrice ?? 0);
  const total = Number(data.priceWithVat ?? 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Flex align="center" gap="md">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Title order={2}>Order #{data.id}</Title>
      </Flex>

      {/* Order info + Customer */}
      <Flex gap="md">
        <Paper withBorder p="md" flex={1}>
          <Title order={3} mb="sm">Order Info</Title>
          <Table withRowBorders={false}>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td><Text fw={600}>Created</Text></Table.Td>
                <Table.Td>{formatDate(data.created)}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text fw={600}>Payment ID</Text></Table.Td>
                <Table.Td>{data.paymentId ?? '—'}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text fw={600}>Invoice</Text></Table.Td>
                <Table.Td>{data.invoiceNumber ?? '—'}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text fw={600}>Status</Text></Table.Td>
                <Table.Td>
                  <Select
                    value={data.orderStatusId !== null && data.orderStatusId !== undefined ? String(data.orderStatusId) : ''}
                    onChange={(val) => {
                      const num = Number(val);
                      if (!Number.isNaN(num)) {
                        updateStatusMutation.mutate({ id: orderId, orderStatusId: num });
                      }
                    }}
                    disabled={updateStatusMutation.isPending}
                    data={[
                      { value: '', label: '— select —' },
                      ...statuses.map((s) => ({
                        value: String(s.id),
                        label: String(s.title),
                      })),
                    ]}
                    w={200}
                  />
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Paper>

        <Paper withBorder p="md" flex={1}>
          <Title order={3} mb="sm">Customer</Title>
          {data.customer ? (
            <Stack gap={2}>
              <Text>{data.customer.name ?? '—'}</Text>
              <Text>{data.customer.email ?? '—'}</Text>
            </Stack>
          ) : (
            <Text c="dimmed">No customer linked</Text>
          )}
        </Paper>
      </Flex>

      {/* Contacts */}
      <Flex gap="md">
        <ContactBlock label="Billing Contact" contact={data.billingContact} showVat />
        <ContactBlock label="Delivery Contact" contact={data.deliveryContact} />
      </Flex>

      {/* Order items */}
      <Box>
        <Title order={3} mb="sm">Order Items</Title>
        <Table withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {['Product', 'Qty', 'Price', 'Serials'].map((col) => (
                <Table.Th key={col}>{col}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.items.map((item) => (
              <Table.Tr key={item.id}>
                <Table.Td>{item.title ?? item.code ?? '—'}</Table.Td>
                <Table.Td>{item.count ?? 1}</Table.Td>
                <Table.Td>{formatPrice(item.price)}</Table.Td>
                <Table.Td style={{ whiteSpace: 'pre-wrap' }}>{item.serials ?? '—'}</Table.Td>
              </Table.Tr>
            ))}
            {data.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text ta="center" c="dimmed" py="md">No items.</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Box>

      {/* Pricing summary */}
      <Box>
        <Title order={3} mb="sm">Pricing</Title>
        <Table withRowBorders={false} w="auto">
          <Table.Tbody>
            <Table.Tr>
              <Table.Td pr="lg">Subtotal</Table.Td>
              <Table.Td ta="right">{formatPrice(subtotal)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td pr="lg">
                Delivery{data.delivery ? ` (${data.delivery.title})` : ''}
              </Table.Td>
              <Table.Td ta="right">{formatPrice(deliveryPrice)}</Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td pr="lg">VAT</Table.Td>
              <Table.Td ta="right">{formatPrice(vat)}</Table.Td>
            </Table.Tr>
            <Table.Tr style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
              <Table.Td pr={48} fw={600}>Total</Table.Td>
              <Table.Td ta="right" fw={600}>{formatPrice(total)}</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Box>

      {/* Internal note */}
      <Box>
        <Title order={3} mb="sm">Internal Note</Title>
        <Stack gap="xs">
          <Textarea
            value={currentNote}
            onChange={(e) => setNote(e.currentTarget.value)}
            rows={4}
          />
          <Box>
            <Button
              onClick={() => updateNoteMutation.mutate({ id: orderId, note: currentNote })}
              loading={updateNoteMutation.isPending}
            >
              Save Note
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Tracking link */}
      <Box>
        <Title order={3} mb="sm">Tracking Link</Title>
        <Flex gap="xs" align="flex-end">
          <TextInput
            value={currentTracking}
            onChange={(e) => setTrackingLink(e.currentTarget.value)}
            placeholder="https://…"
            flex={1}
          />
          <Button
            onClick={() =>
              updateTrackingMutation.mutate({ id: orderId, trackingLink: currentTracking })
            }
            loading={updateTrackingMutation.isPending}
          >
            Save Tracking
          </Button>
        </Flex>
      </Box>
    </Stack>
  );
}
